import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    this.animateAddToCart();

    if (!event.target.closest('.quick-add-modal')) this.#animateFlyToCart();
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    console.log('[PRODUCT-FORM] handleSubmit called');
    const { addToCartTextError } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    // Check if the add to cart button is disabled and do an early return if it is
    if (this.refs.addToCartButtonContainer?.refs.addToCartButton?.getAttribute('disabled') === 'true') {
      console.log('[PRODUCT-FORM] Button is disabled, returning');
      return;
    }

    // Check for optional extras validation
    const optionalExtras = document.querySelector('optional-extras-picker');
    if (optionalExtras && optionalExtras.hasMandatoryGroups && optionalExtras.hasMandatoryGroups()) {
      console.log('[PRODUCT-FORM] Checking optional extras validation...');
      if (!optionalExtras.validateMandatorySelections()) {
        console.log('[PRODUCT-FORM] Optional extras validation failed, preventing submission');
        
        // Prevent button state change
        const addButton = this.querySelector('[name="add"]');
        if (addButton) {
          // Preserve original button state
          const originalText = addButton.textContent;
          const originalHTML = addButton.innerHTML;
          
          // Reset button if it changes
          setTimeout(() => {
            if (addButton.textContent !== originalText) {
              addButton.innerHTML = originalHTML;
            }
          }, 10);
        }
        
        return;
      }
    }

    console.log('[PRODUCT-FORM] Sending add to cart request');
    // Send the add to cart information to the cart
    const form = this.querySelector('form');

    if (!form) throw new Error('Product form element missing');

    // Debug: Check all inputs in the form
    console.log('[PRODUCT-FORM] All form inputs:');
    const allInputs = form.querySelectorAll('input, select, textarea');
    allInputs.forEach(input => {
      console.log(`[PRODUCT-FORM]   ${input.name}: ${input.value} (type: ${input.type})`);
    });

    const formData = new FormData(form);
    
    // Collect all properties from the form
    const allProperties = {};
    for (let [key, value] of formData.entries()) {
      if (key.startsWith('properties[')) {
        allProperties[key] = value;
      }
    }
    
    // Add measurement properties if a custom size is selected
    const customSizeInput = form.querySelector('input[name="properties[Custom Size]"]');
    if (customSizeInput && customSizeInput.value) {
      console.log('[PRODUCT-FORM] Custom size selected:', customSizeInput.value);
      
      // Get the custom size data from localStorage
      try {
        const customSizes = JSON.parse(localStorage.getItem('customSizes') || '[]');
        const selectedSize = customSizes.find(size => size.name === customSizeInput.value);
        
        if (selectedSize && selectedSize.measurements) {
          console.log('[PRODUCT-FORM] Found measurements for custom size:', selectedSize);
          
          // Add measurement properties with underscore prefix (hidden in cart)
          const measurementFields = [
            { key: 'chest', name: '_Chest' },
            { key: 'shoulders', name: '_Shoulders' },
            { key: 'hips', name: '_Hips' },
            { key: 'waist', name: '_Waist' },
            { key: 'sleeves', name: '_Sleeves' },
            { key: 'trouser_length', name: '_Trouser Length' },
            { key: 'shirt_length', name: '_Shirt Length' }
          ];
          
          measurementFields.forEach(field => {
            const value = selectedSize.measurements[field.key];
            if (value !== null && value !== undefined) {
              const propertyKey = `properties[${field.name}]`;
              const measurementValue = `${value} ${selectedSize.unit || 'cm'}`;
              allProperties[propertyKey] = measurementValue;
              console.log(`[PRODUCT-FORM] Added measurement: ${field.name} = ${measurementValue}`);
            }
          });
        }
      } catch (error) {
        console.error('[PRODUCT-FORM] Error loading custom size measurements:', error);
      }
    }
    
    // Calculate Est Ship Date before ordering
    const estShipDate = this.calculateEstimatedShipDate();
    if (estShipDate) {
      // Add with underscore to hide from cart display
      allProperties['properties[_Est Ship Date]'] = estShipDate;
      console.log('[PRODUCT-FORM] Added Est Ship Date:', estShipDate);
    }
    
    // Define the correct priority order
    const propertyOrder = [
      'Custom Size',
      '_Chest', '_Shoulders', '_Hips', '_Waist', '_Sleeves', '_Trouser Length', '_Shirt Length', // Hidden measurements
      'Custom Color',
      'Color Code',  // Directly after Custom Color
      'Sleeves',
      'Optional Extras',
      '_Est Ship Date'  // Hidden with underscore
    ];
    
    // Create a new FormData with proper ordering
    const orderedFormData = new FormData();
    
    // First, add non-property fields
    for (let [key, value] of formData.entries()) {
      if (!key.startsWith('properties[')) {
        orderedFormData.append(key, value);
      }
    }
    
    // Get Color Code from DOM if not in properties yet
    const colorCodeInput = document.getElementById('selected-custom-color-code');
    if (colorCodeInput && colorCodeInput.value && !allProperties['properties[Color Code]']) {
      allProperties['properties[Color Code]'] = colorCodeInput.value;
      console.log('[PRODUCT-FORM] Added Color Code from DOM:', colorCodeInput.value);
    }
    
    // Process each property in order
    propertyOrder.forEach(prop => {
      const propertyKey = `properties[${prop}]`;
      
      // Check if property exists in allProperties
      if (allProperties[propertyKey]) {
        // Special handling for Custom Color - remove prefix if it exists
        if (prop === 'Custom Color') {
          let colorValue = allProperties[propertyKey];
          // Remove "Custom Color: " prefix if present
          if (colorValue.startsWith('Custom Color: ')) {
            colorValue = colorValue.replace('Custom Color: ', '');
          }
          orderedFormData.append(propertyKey, colorValue);
          delete allProperties[propertyKey];
          
          // Add Color Code immediately after Custom Color
          if (allProperties['properties[Color Code]']) {
            orderedFormData.append('properties[Color Code]', allProperties['properties[Color Code]']);
            delete allProperties['properties[Color Code]'];
            console.log('[PRODUCT-FORM] Added Color Code right after Custom Color');
          }
        } else if (prop !== 'Color Code') { // Skip Color Code in normal processing since we handle it with Custom Color
          orderedFormData.append(propertyKey, allProperties[propertyKey]);
          delete allProperties[propertyKey];
        }
      }
      // Also check for measurements without underscore (add underscore if found)
      else if (prop.startsWith('_')) {
        const propWithoutUnderscore = prop.substring(1);
        const keyWithoutUnderscore = `properties[${propWithoutUnderscore}]`;
        if (allProperties[keyWithoutUnderscore]) {
          // Add with underscore to hide from cart display
          orderedFormData.append(propertyKey, allProperties[keyWithoutUnderscore]);
          delete allProperties[keyWithoutUnderscore];
        }
      }
    });
    
    // Add any remaining properties not in our order list
    for (let [key, value] of Object.entries(allProperties)) {
      // Skip if already handled
      if (!orderedFormData.has(key)) {
        orderedFormData.append(key, value);
      }
    }
    
    // Handle Custom Color from input if not already added
    const customColorInput = document.getElementById('custom-color-display-input');
    
    // Check if Custom Color needs to be added (rare case where it's not in form data)
    if (!orderedFormData.has('properties[Custom Color]') && customColorInput && customColorInput.value) {
      let colorValue = customColorInput.value;
      // Remove "Custom Color: " prefix if present
      if (colorValue.startsWith('Custom Color: ')) {
        colorValue = colorValue.replace('Custom Color: ', '');
      }
      orderedFormData.append('properties[Custom Color]', colorValue);
      console.log('[PRODUCT-FORM] Added Custom Color from fallback:', colorValue);
      
      // Also check for Color Code if Custom Color was added
      if (colorCodeInput && colorCodeInput.value && !orderedFormData.has('properties[Color Code]')) {
        orderedFormData.append('properties[Color Code]', colorCodeInput.value);
        console.log('[PRODUCT-FORM] Added Color Code from fallback:', colorCodeInput.value);
      }
    }
    
    // Log form data
    console.log('[PRODUCT-FORM] Form data being sent:');
    for (let [key, value] of orderedFormData.entries()) {
      console.log(`[PRODUCT-FORM]   ${key}: ${value}`);
    }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      orderedFormData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const fetchCfg = fetchConfig('javascript', { body: orderedFormData });
    console.log('[PRODUCT-FORM] Fetch config:', fetchCfg);

    console.log('[PRODUCT-FORM] Fetching to:', Theme.routes.cart_add_url);
    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => {
        console.log('[PRODUCT-FORM] Response status:', response.status);
        console.log('[PRODUCT-FORM] Response headers:', response.headers.get('content-type'));
        return response.json();
      })
      .then((response) => {
        console.log('[PRODUCT-FORM] Response data:', response);
        
        // Log the properties that were actually saved
        if (response.properties) {
          console.log('[PRODUCT-FORM] Properties saved to cart:');
          Object.entries(response.properties).forEach(([key, value]) => {
            console.log(`[PRODUCT-FORM]   ${key}: ${value}`);
          });
        } else {
          console.log('[PRODUCT-FORM] No properties in response');
        }
        
        if (response.status) {
          window.dispatchEvent(new CartErrorEvent(this.id, response.message));

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, 10000);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
            const addToCartButton = this.refs.addToCartButtonContainer.refs.addToCartButton;
            const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, 5000);
          }

          console.log('[PRODUCT-FORM] Dispatching CartAddEvent:', {
            variantId: id.toString(),
            source: 'product-form-component',
            itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
            productId: this.dataset.productId,
            sections: response.sections
          });
          
          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        // add more thing to do in here if needed.
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;

    const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton;
    const newAddToCartButton = event.detail.data.html.querySelector('[ref="addToCartButton"]');

    if (!currentAddToCartButton) return;

    // Update the button state
    if (event.detail.resource == null || event.detail.resource.available == false) {
      addToCartButtonContainer.disable();
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
    } else {
      addToCartButtonContainer.enable();
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
    }

    // Update the add to cart button text and icon
    if (newAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    // Update the variant ID
    variantId.value = event.detail.resource.id ?? '';

    // Set the data attribute for the add to cart button to the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');
    }
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };

  /**
   * Calculate estimated ship date based on inventory status
   * @returns {string} The estimated ship date in format "DD Month YYYY"
   */
  calculateEstimatedShipDate() {
    try {
      // Get the current variant ID from the form
      const variantIdInput = this.querySelector('input[name="id"]');
      const currentVariantId = variantIdInput ? variantIdInput.value : null;
      
      console.log('[PRODUCT-FORM] Calculating Est Ship Date for variant:', currentVariantId);
      
      // Get the product inventory element
      const inventoryElement = document.querySelector('product-inventory');
      if (!inventoryElement) {
        console.log('[PRODUCT-FORM] No inventory element found, using default ship date');
        return this.getDefaultShipDate();
      }

      // Get inventory data from data attributes
      let inventoryQuantity = parseInt(inventoryElement.dataset.inventoryQuantity) || 0;
      const inventoryThreshold = parseInt(inventoryElement.dataset.inventoryThreshold) || 10;
      
      // Check the inventory status text as a more reliable indicator
      const inventoryText = inventoryElement.querySelector('.product-inventory__text')?.textContent || '';
      
      // Try to extract quantity from text if it says "X in stock"
      const inStockMatch = inventoryText.match(/(\d+)\s+in\s+stock/i);
      if (inStockMatch) {
        inventoryQuantity = parseInt(inStockMatch[1]);
        console.log('[PRODUCT-FORM] Extracted quantity from text:', inventoryQuantity);
      }
      
      const isBackorder = inventoryText.toLowerCase().includes('backorder') || 
                         inventoryText.toLowerCase().includes('custom order') ||
                         inventoryText.toLowerCase().includes('weeks');
      
      console.log('[PRODUCT-FORM] Inventory text:', inventoryText);
      console.log('[PRODUCT-FORM] Inventory quantity:', inventoryQuantity);
      console.log('[PRODUCT-FORM] Is backorder (from text):', isBackorder);
      
      // Determine if item is in stock or backorder
      // If text explicitly says "in stock" and we have quantity, it's in stock
      const hasInStockText = inventoryText.toLowerCase().includes('in stock');
      const isInStock = hasInStockText || (!isBackorder && inventoryQuantity > 0);
      
      if (isInStock) {
        // For in-stock items, use TODAY + 4 days
        console.log('[PRODUCT-FORM] Item is in stock, using TODAY + 4 days');
        return this.formatShipDate(4);
      } else {
        // For backorder items, we need to get the production days
        // Try to get it from the inventory block settings
        const productionDays = this.getBackorderProductionDays();
        console.log('[PRODUCT-FORM] Item is on backorder, using TODAY + ' + productionDays + ' days');
        return this.formatShipDate(productionDays);
      }
    } catch (error) {
      console.error('[PRODUCT-FORM] Error calculating ship date:', error);
      return this.getDefaultShipDate();
    }
  }

  /**
   * Get backorder production days from settings or use default
   * @returns {number} The number of production days
   */
  getBackorderProductionDays() {
    // Try to get from window object if exposed by Liquid
    if (window.backorderProductionDays !== undefined) {
      return parseInt(window.backorderProductionDays) || 30;
    }
    
    // Default to 30 days if not found
    return 30;
  }

  /**
   * Get default ship date (TODAY + 4 days)
   * @returns {string} The default ship date
   */
  getDefaultShipDate() {
    return this.formatShipDate(4);
  }

  /**
   * Format ship date as DD Month YYYY
   * @param {number} daysToAdd - Number of days to add to today
   * @returns {string} Formatted ship date
   */
  formatShipDate(daysToAdd) {
    const date = new Date();
    date.setDate(date.getDate() + daysToAdd);
    
    const day = date.getDate();
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                   'July', 'August', 'September', 'October', 'November', 'December'];
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    
    return `${day} ${month} ${year}`;
  }
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    //Define bezier curve points
    // Maybe add half of the size of the flying thingy to the x and y to make it center properly
    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    //Calculate the control points
    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 }; // Go up 200px
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 }; // Go left 300px and up 100px

    //Animation variables
    /** @type {number | null} */
    let startTime = null;
    const duration = 600; // 600ms

    this.style.opacity = '1';

    /**
     * Animates the flying thingy along the bezier curve.
     * @param {number} currentTime - The current time.
     */
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Calculate current position along the bezier curve
      const position = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);

      //Update the position of the flying thingy
      this.style.setProperty('--x', `${position.x}px`);
      this.style.setProperty('--y', `${position.y}px`);

      // Scale down as it approaches the cart
      const scale = 1 - progress * 0.5;
      this.style.setProperty('--scale', `${scale}`);

      //Continue the animation if not finished
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        //Fade out the flying thingy
        this.style.opacity = '0';
        onAnimationEnd(this, () => this.remove());
      }
    };

    // Position the flying thingy back to the start point
    this.style.setProperty('--x', `${startPoint.x}px`);
    this.style.setProperty('--y', `${startPoint.y}px`);

    //Start the animation
    requestAnimationFrame(animate);
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
